import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { Logger } from '@core/lib/services';

const log = new Logger('SseSearchFilterSortService');

@Injectable({
  providedIn: 'root'
})
export class SseSearchFilterSortService {
  /************************** Private Functions **************************/

  /**
   * to escape the special character from the like expression
   * @param likeExp
   * @returns {*}
   */
  escapeCharacterInRegex(likeExp) {
    const specials = ['/', '.', '+', '?', '|', '(', ')', '[', ']', '{', '}', '\\'];
    const specialsRegEx = new RegExp('(\\' + specials.join('|\\') + ')', 'g');
    return likeExp.replace(specialsRegEx, '\\$1');
  }

  /**
   * to replace * OR % from begin or end of string
   * @param likeExp
   * @returns {*}
   */
  replaceStarORAmpFromBeginOrEndInLikeExp(likeExp) {
    if (
      (likeExp.indexOf('*') === 0 || likeExp.indexOf('%') === 0) &&
      (likeExp.lastIndexOf('*') === likeExp.length - 1 || likeExp.lastIndexOf('%') === likeExp.length - 1)
    ) {
      likeExp = '.*' + likeExp.slice(1, -1) + '.*';
    } else if (likeExp.indexOf('*') === 0 || likeExp.indexOf('%') === 0) {
      likeExp = likeExp.slice(1) + '$';
    } else if (likeExp.indexOf('*') === likeExp.length - 1 || likeExp.indexOf('%') === likeExp.length - 1) {
      likeExp = '^' + likeExp.slice(0, -1);
    } else {
      likeExp = '^' + likeExp;
    }
    return likeExp;
  }

  /**
   * to check the sub string matched the value
   * @param value
   * @param subStr
   * @returns {boolean}
   */
  checkStringPresentIgnoreCase(value, subStr) {
    if (value === undefined || value === '' || subStr === undefined || subStr === '') {
      return false;
    }
    let likeExp = this.escapeCharacterInRegex(subStr.toLowerCase());
    likeExp = this.replaceStarORAmpFromBeginOrEndInLikeExp(likeExp);
    const isMatchFound = new RegExp(likeExp.replace(/%/g, '.*').replace(/_/g, '.')).exec(value.toLowerCase()) != null;
    return isMatchFound;
  }

  /**
   * to check the any of the sub array string matched the value
   * @param value
   * @param subStrArr
   * @returns {boolean}
   */
  checkAnyStringFromArrayPresentIgnoreCase(value, subStrArr) {
    let isMatchFound = false;
    if (value === undefined || value === '' || subStrArr === undefined || subStrArr.length === 0) {
      return isMatchFound;
    }
    for (let i = 0; i < subStrArr.length; ++i) {
      if ('string' === typeof value) {
        const likeExp = this.escapeCharacterInRegex(subStrArr[i].toLowerCase());
        isMatchFound =
          new RegExp(likeExp.replace('\\*', '.*').replace(/%/g, '.*').replace(/_/g, '.')).exec(value.toLowerCase()) !=
          null;
      } else {
        isMatchFound = value === subStrArr[i];
      }
      if (isMatchFound) {
        break;
      }
    }
    return isMatchFound;
  }

  /**
   * to format date
   * @param date
   * @returns {*}
   */
  refactorDate(date) {
    date = date.trim();
    date = date.replace(' ', 'T');
    const dateArr = date.split('.');
    if (dateArr && dateArr.length === 2) {
      let milliSec = dateArr[1];
      for (let i = dateArr[1].length; i < 3; i++) {
        milliSec = milliSec + 0;
      }
      dateArr[1] = milliSec;
      date = dateArr.join('.');
    }
    return date;
  }

  /**
   * to check if date within date range
   * @param dob
   * @param fromDate
   * @param toDate
   * @returns {boolean}
   */
  checkIfDateWithinDate(date, fromDate, toDate) {
    date = this.refactorDate(date);
    date = new Date(date);
    fromDate = new Date(this.refactorDate(fromDate));
    toDate = new Date(this.refactorDate(toDate));
    return date >= fromDate && date <= toDate;
  }

  /**
   * to check if a date within date range
   * @param date
   * @param timeRange
   * @returns {boolean}
   */
  checkIfDateWithinTimeRange(date, timeRange) {
    date = this.refactorDate(date);
    date = new Date(date);
    const today = new Date();
    const computeDate = new Date();
    computeDate.setTime(today.getTime() - timeRange * 3600000);
    return date >= computeDate && date <= today;
  }

  /**
   * to sort and filter search result
   * @param filterAndSortCriteria
   * @returns {*}
   */
  getFilteredAndSortedResults(data, filterAndSortCriteria) {
    const searchResult = data;
    const clonedSearchResult = _.clone(searchResult);
    if (!!filterAndSortCriteria.filterParams.filter) {
      clonedSearchResult.studies = this.getFilteredResult(
        clonedSearchResult.studies,
        filterAndSortCriteria.filterParams.filter
      );
    }
    if (!!filterAndSortCriteria.filterParams.sort) {
      clonedSearchResult.studies = this.getSortedResult(clonedSearchResult.studies, filterAndSortCriteria.filterParams);
    }
    return clonedSearchResult;
  }

  /**
   * to get sorted studies
   * @param studies
   * @param sortCriteria
   * @returns {*}
   */
  getSortedResult(studies, sortCriteria) {
    const sortItem = sortCriteria.sort.item;
    const priorityArr = sortCriteria.priorityArr;
    if (sortItem === undefined) {
      return studies;
    }
    const sortedStudies = studies.sort((study1, study2) => {
      if (Object.keys(sortItem).length === 2) {
        return (
          this.applySortByPriority(
            study1.procedurePriority || 'Unknown',
            study2.procedurePriority || 'Unknown',
            priorityArr,
            sortItem.priority
          ) || this.applySort(study1, study2, sortItem, priorityArr)
        );
      } else {
        return this.applySort(study1, study2, sortItem, priorityArr);
      }
    });
    log.debug('Sort Criteria' + sortItem);
    return sortedStudies ? sortedStudies : studies;
  }

  /**
   * to apply sort
   * @param study1
   * @param study2
   * @param sortItem
   * @param priorityArr
   * @returns {number}
   */
  applySort(study1, study2, sortItem, priorityArr) {
    let result = this.applySortByMRN(study1, study2, sortItem);
    result = this.applySortUsingFirstListOfProperties(result, study1, study2, sortItem, priorityArr);
    result = this.applySortUsingSecondListOfProperties(result, study1, study2, sortItem);
    result = this.applySortUsingThirdListOfProperties(result, study1, study2, sortItem);
    return result;
  }

  /**
   *
   * @param study1
   * @param study2
   * @param sortItem
   * @returns {number}
   */
  applySortByMRN(study1, study2, sortItem) {
    let result = 0;
    if (!!sortItem.mrn) {
      const mrnList1 = _.pluck(study1.workItem.patientIdentifier, 'value');
      const mrnList2 = _.pluck(study2.workItem.patientIdentifier, 'value');
      result = this.applySortByString(mrnList1[0], mrnList2[0], sortItem.mrn);
    }
    return result;
  }

  /**
   *
   * @param result
   * @param study1
   * @param study2
   * @param sortItem
   * @param priorityArr
   * @returns {number}
   */
  applySortUsingFirstListOfProperties(result, study1, study2, sortItem, priorityArr) {
    if (!!sortItem.priority) {
      result = this.applySortByPriority(
        study1.procedurePriority || 'Unknown',
        study2.procedurePriority || 'Unknown',
        priorityArr,
        sortItem.priority
      );
    }
    if (!!sortItem.patientVipCode) {
      result = this.applySortByString(
        study1.workItem.patientVipCode,
        study2.workItem.patientVipCode,
        sortItem.patientVipCode
      );
    }
    if (
      !!sortItem.referringPhysician &&
      !!study1.workItem.procedureReferringPhysician &&
      !!study2.workItem.procedureReferringPhysician
    ) {
      result = this.applySortByString(
        study1.workItem.procedureReferringPhysician.name.text,
        study2.workItem.procedureReferringPhysician.name.text,
        sortItem.referringPhysician
      );
    }
    if (!!sortItem.patientName) {
      result = this.applySortByString(
        study1.workItem.patientName.text,
        study2.workItem.patientName.text,
        sortItem.patientName
      );
    }
    if (!!sortItem.patientDoB) {
      result = this.applySortByDate(
        study1.workItem.patientBirthDate,
        study2.workItem.patientBirthDate,
        sortItem.patientDoB
      );
    }
    return result;
  }

  /**
   * to apply sort from second list
   * @param study1
   * @param study2
   * @param sortItem
   * @param priorityArr
   * @returns {number}
   */
  applySortUsingSecondListOfProperties(result, study1, study2, sortItem) {
    if (!!sortItem.modality) {
      result = this.applySortByString(
        study1.workItem.procedureModality,
        study2.workItem.procedureModality,
        sortItem.modality
      );
    }
    if (!!sortItem.department) {
      result = this.applySortByString(
        study1.workItem.departmentName,
        study2.workItem.departmentName,
        sortItem.department
      );
    }
    if (!!sortItem.status) {
      result = this.applySortByString(
        study1.workItem.procedureStatus,
        study2.workItem.procedureStatus,
        sortItem.status
      );
    }
    if (!!sortItem.imageCount) {
      result = this.applySortByString(study1.workItem.imageCount, study2.workItem.imageCount, sortItem.imageCount);
    }
    if (!!sortItem.procedureText) {
      result = this.applySortByString(
        study1.workItem.procedureText,
        study2.workItem.procedureText,
        sortItem.procedureText
      );
    }
    if (!!sortItem.patientVipCode) {
      result = this.applySortByString(
        study1.workItem.patientVipCode,
        study2.workItem.patientVipCode,
        sortItem.patientVipCode
      );
    }
    if (!!sortItem.accession) {
      result = this.applySortByString(
        study1.workItem.procedureAccessionNo,
        study2.workItem.procedureAccessionNo,
        sortItem.accession
      );
    }
    return result;
  }
  /**
   * to apply sort from third list
   * @param study1
   * @param study2
   * @param sortItem
   * @param priorityArr
   * @returns {number}
   */
  applySortUsingThirdListOfProperties(result, study1, study2, sortItem) {
    if (!!sortItem.timeOfExam) {
      result = this.applySortByDate(
        study1.workItem.procedureStartTime,
        study2.workItem.procedureStartTime,
        sortItem.timeOfExam
      );
    }
    if (!!sortItem.Location) {
      result = this.applySortByString(study1.workItem.repositoryId, study2.workItem.repositoryId, sortItem.Location);
    }
    if (!!sortItem.orderingLocation) {
      if (!!study1.workItem.orderingLocation && study1.workItem.orderingLocation === 'Unspecified') {
        study1.workItem.orderingLocation = 'No Information Available';
      }
      if (!!study2.workItem.orderingLocation && study2.workItem.orderingLocation === 'Unspecified') {
        study2.workItem.orderingLocation = 'No Information Available';
      }
      result = this.applySortByString(
        study1.workItem.orderingLocation,
        study2.workItem.orderingLocation,
        sortItem.orderingLocation
      );
    }
    return result;
  }
  /**
   * to apply sort based on string
   * @param value1
   * @param value2
   * @param sortType
   * @returns {number}
   */
  applySortByString(value1, value2, sortType) {
    if (!value1) {
      value1 = 'Unspecified';
    }
    if (!value2) {
      value2 = 'Unspecified';
    }
    const result = value1 === value2 ? 0 : value1 < value2 ? -1 : 1;
    if ('ASC' === sortType) {
      return result;
    } else {
      return -1 * result;
    }
  }

  /**
   * to apply sort based on date
   * @param value1
   * @param value2
   * @param sortType
   * @returns {number}
   */
  applySortByDate(value1, value2, sortType) {
    if (!value1) {
      value1 = new Date();
    } else {
      value1 = this.refactorDate(value1);
      value1 = new Date(value1);
    }

    if (!value2) {
      value2 = new Date();
    } else {
      value2 = this.refactorDate(value2);
      value2 = new Date(value2);
    }
    const result = value1 - value2;
    if ('ASC' === sortType) {
      return result;
    } else {
      return -1 * result;
    }
  }

  /**
   * Funtion to sort studies by priority
   * @param procedureName1
   * @param procedureName2
   * @param priorityArr
   * @param sortType
   * @returns {number}
   */
  applySortByPriority(procedureName1, procedureName2, priorityArr, sortType) {
    const priorityId1 = this.getPriorityIdFromName(procedureName1, priorityArr);
    const priorityId2 = this.getPriorityIdFromName(procedureName2, priorityArr);
    const result = priorityId1 - priorityId2;
    if ('ASC' === sortType) {
      return result;
    } else {
      return -1 * result;
    }
  }

  /**
   * to get priority id from priority name
   * @param name
   * @param priorityArr
   * @returns {*}
   */
  getPriorityIdFromName(name, priorityArr) {
    const priorityIdArr = _.pluck(
      _.filter(priorityArr, p => {
        return p.priorityName.toLowerCase() === name.toLowerCase();
      }),
      'priorityId'
    );
    if (priorityIdArr.length >= 1) {
      return priorityIdArr[0];
    } else {
      return -1;
    }
  }

  /**
   * to filter studies based on date
   * @param study
   * @param isFilterMatched
   * @param filterCriteria
   * @returns {*}
   */
  getFilteredResultUsingDate(study, isFilterMatched, filterCriteria) {
    if (isFilterMatched && !!filterCriteria.dobFromDate && !!filterCriteria.dobToDate) {
      isFilterMatched = this.checkIfDateWithinDate(
        study.workItem.patientBirthDate,
        filterCriteria.dobFromDate,
        filterCriteria.dobToDate
      );
    }
    if (isFilterMatched && !!filterCriteria.fromDate && !!filterCriteria.toDate) {
      isFilterMatched = this.checkIfDateWithinDate(
        study.workItem.procedureStartTime,
        filterCriteria.fromDate,
        filterCriteria.toDate
      );
    }
    if (isFilterMatched && !!filterCriteria.timeRange) {
      isFilterMatched = this.checkIfDateWithinTimeRange(study.workItem.procedureStartTime, filterCriteria.timeRange);
    }
    return isFilterMatched;
  }

  /**
   * to filter studies based on array of sub string
   * @param study array
   * @param isFilterMatched
   * @param filterCriteria
   * @returns {*}
   */
  getFilteredResultUsingArrayOfSubString(study, isFilterMatched, filterCriteria) {
    if (isFilterMatched && !!filterCriteria.priority && filterCriteria.priority.length !== 0) {
      isFilterMatched = this.checkAnyStringFromArrayPresentIgnoreCase(
        study.procedurePriority || 'Unknown',
        _.pluck(filterCriteria.priority, 'name')
      );
    }
    if (isFilterMatched && !!filterCriteria.status) {
      isFilterMatched = this.checkAnyStringFromArrayPresentIgnoreCase(
        study.workItem.procedureStatus,
        filterCriteria.status
      );
    }
    if (isFilterMatched && !!filterCriteria.modality) {
      isFilterMatched = this.checkAnyStringFromArrayPresentIgnoreCase(
        study.workItem.procedureModality,
        filterCriteria.modality
      );
    }
    if (isFilterMatched && !!filterCriteria.referringPhysician && !!study.workItem.procedureReferringPhysician) {
      isFilterMatched = this.checkAnyStringFromArrayPresentIgnoreCase(
        study.workItem.procedureReferringPhysician.practitionerId,
        filterCriteria.referringPhysician
      );
    }
    if (isFilterMatched && !!filterCriteria.orderingLocation && filterCriteria.orderingLocation.length !== 0) {
      if (study.workItem.orderingLocation === 'Unspecified') {
        study.workItem.orderingLocation = 'No Information Available';
      }
      isFilterMatched = this.checkAnyStringFromArrayPresentIgnoreCase(
        study.workItem.orderingLocation,
        _.pluck(filterCriteria.orderingLocation, 'name')
      );
    }
    return isFilterMatched;
  }

  /**
   * to filter studies based on MRN
   * @param study
   * @param isFilterMatched
   * @param filterCriteria
   * @returns {*}
   */
  getFilteredByMRN(study, isFilterMatched, filterCriteria) {
    if (isFilterMatched && !!filterCriteria.mrn) {
      const mrnList = _.pluck(study.workItem.patientIdentifier, 'value');
      for (let i = 0; i < mrnList.length; ++i) {
        isFilterMatched = this.checkStringPresentIgnoreCase(mrnList[i], filterCriteria.mrn[0]);
        if (isFilterMatched) {
          break;
        }
      }
    }
    return isFilterMatched;
  }
  /**
   * to filter studies based on image count filter
   * @param study
   * @param isFilterMatched
   * @param filterCriteria
   * @returns {*}
   */
  getFilteredImageCount(study, isFilterMatched, filterCriteria) {
    if (isFilterMatched && !!filterCriteria.imageCount) {
      isFilterMatched =
        study.workItem.archiveAdoptedStatus !== 'NO' ||
        study.workItem.hasReport === true ||
        study.workItem.imageCount > 0;
    }
    return isFilterMatched;
  }
  /**
   * to filter results
   * @param studies
   * @param filterCriteria
   * @returns {*}
   */
  getFilteredResult(studies, filterCriteria) {
    const filteredData = _.filter(studies, study => {
      let isFilterMatched = true;
      isFilterMatched = this.getFilteredByMRN(study, isFilterMatched, filterCriteria);
      isFilterMatched = this.getFilteredImageCount(study, isFilterMatched, filterCriteria);
      if (!!filterCriteria.patientName) {
        isFilterMatched = this.checkStringPresentIgnoreCase(
          study.workItem.patientName.text,
          filterCriteria.patientName[0]
        );
      }
      if (isFilterMatched && !!filterCriteria.Location) {
        isFilterMatched = this.checkStringPresentIgnoreCase(study.workItem.repositoryId, filterCriteria.Location[0]);
      }
      if (isFilterMatched && !!filterCriteria.department && filterCriteria.department.length !== 0) {
        isFilterMatched = this.checkAnyStringFromArrayPresentIgnoreCase(
          study.workItem.departmentName,
          _.pluck(filterCriteria.department, 'name')
        );
      }
      if (isFilterMatched && !!filterCriteria.procedureText) {
        isFilterMatched = this.checkStringPresentIgnoreCase(
          study.workItem.procedureText,
          filterCriteria.procedureText[0]
        );
      }
      if (isFilterMatched && !!filterCriteria.accession) {
        isFilterMatched = this.checkStringPresentIgnoreCase(
          study.workItem.procedureAccessionNo,
          filterCriteria.accession[0]
        );
      }
      isFilterMatched = this.getFilteredResultUsingDate(study, isFilterMatched, filterCriteria);
      isFilterMatched = this.getFilteredResultUsingArrayOfSubString(study, isFilterMatched, filterCriteria);
      return isFilterMatched;
    });
    return filteredData;
  }
}
